import {
  Box,
  HStack,
  Progress,
  SimpleGrid,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { formatMetricValue } from '../analyticsMap/choropleth'
import { METRIC_OPTIONS } from '../../types/geoAnalytics'
import ProfileCard from './ProfileCard'
import { StatTile, seriesColor, vizSeqVar } from '../shared/viz'

interface ElectorateCardProps {
  acs: { metricKey: string; value: number | null }[]
}

const numberFmt = new Intl.NumberFormat('en-US')

const SEQ_STEPS = [100, 200, 300, 400, 500, 600, 700] as const

/** Ordinal-magnitude color: maps a bracket's position within an ordered series (income/age/
 *  education) to the nearest of the 7 sequential steps — same idiom as DistributionCharts'
 *  seqColorForBucket (analyticsMap dashboard, A6 slice 9 Task 10). */
function seqColorForBucket(index: number, total: number): string {
  const t = total > 1 ? index / (total - 1) : 0.5
  const stepIndex = Math.min(SEQ_STEPS.length - 1, Math.floor(t * SEQ_STEPS.length))
  return vizSeqVar(SEQ_STEPS[stepIndex])
}

/** One percent-share bar row: wrapping label (with full-text tooltip), fluid bar, right-aligned value. */
export function ShareBarRow({
  label,
  value,
  text,
  colorVar,
}: {
  label: string
  value: number
  text: string
  colorVar: string
}) {
  return (
    <HStack gap={3} mb={1.5} align="center">
      <Text fontSize="sm" w="180px" flexShrink={0} color="gray.700" lineClamp={2} title={label}>
        {label}
      </Text>
      <Progress.Root
        flex="1"
        value={value}
        max={100}
        size="sm"
        colorPalette="brand"
        borderRadius="sm"
        aria-label={`${label} share`}>
        <Progress.Track>
          <Progress.Range bg={colorVar} />
        </Progress.Track>
      </Progress.Root>
      <Text fontSize="sm" w="60px" flexShrink={0} textAlign="right" fontWeight="medium">
        {text}
      </Text>
    </HStack>
  );
}

interface ShareRow {
  key: string
  label: string
  value: number
  text: string
  colorVar: string
}

/**
 * Build sorted (desc, for display) share-bar rows for the given metric keys, dropping any
 * that are null/absent. `colorVar` is assigned from each key's position in the INPUT `keys`
 * array (its natural bracket/category order), not the value-sorted display order, so a given
 * bracket/category keeps the same color regardless of which district is loaded.
 * `mode: 'ordinal'` (income/age/education brackets) walks the 7 viz.seq steps by position;
 * `mode: 'nominal'` (race/industry/occupation/economy-indicator families, no inherent order)
 * cycles the categorical viz.cat palette.
 */
function shareRows(byKey: Map<string, number | null>, keys: string[], mode: 'ordinal' | 'nominal'): ShareRow[] {
  return keys
    .map((key, i) => {
      const value = byKey.get(key)
      if (value == null) return null
      const option = METRIC_OPTIONS.find((o) => o.key === key)
      return {
        key,
        label: option?.label ?? key,
        value,
        text: option ? formatMetricValue(option, value, false) : `${value.toFixed(1)}%`,
        colorVar: mode === 'ordinal' ? seqColorForBucket(i, keys.length) : seriesColor(i),
      }
    })
    .filter((r): r is ShareRow => r !== null)
    .sort((a, b) => b.value - a.value)
}

/** A captioned group of share bars; shows an honest empty line when the family has no data. */
function ShareGroup({ caption, rows }: { caption: string; rows: ShareRow[] }) {
  return (
    <Box mb={4}>
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="0.1em"
        textTransform="uppercase"
        color="gray.500"
        mb={2}
      >
        {caption}
      </Text>
      {rows.length > 0 ? (
        rows.map((row) => (
          <ShareBarRow key={row.key} label={row.label} value={row.value} text={row.text} colorVar={row.colorVar} />
        ))
      ) : (
        <Text fontSize="sm" color="gray.400">
          Not available for this geography.
        </Text>
      )}
    </Box>
  )
}

/** Overview headline tiles, in order. `prefix` renders in front of the formatted value. */
const STAT_TILES: { key: string; label: string; prefix?: string }[] = [
  { key: 'acs.population.total', label: 'Population' },
  { key: 'acs.median_income', label: 'Median HH income', prefix: '$' },
  { key: 'acs.median_age', label: 'Median age' },
  { key: 'acs.college_share', label: 'BA or higher' },
]

const AGE_KEYS = ['acs.age18_29_share', 'acs.age65_share']
const RACE_KEYS = ['acs.race.white', 'acs.race.black', 'acs.race.hispanic', 'acs.race.asian']
const EDUCATION_KEYS = ['acs.college_share']
const ECONOMY_BAR_KEYS = ['acs.poverty_share', 'acs.renter_share']

export function ElectorateCard({ acs }: ElectorateCardProps) {
  const byKey = new Map(acs.map((a) => [a.metricKey, a.value]))
  const hasAnything = acs.some((a) => a.value != null)

  // Overview tiles (label from STAT_TILES; value formatted via catalog or numberFmt fallback).
  const tiles = STAT_TILES.map((tile) => {
    const value = byKey.get(tile.key)
    if (value == null) return null
    const option = METRIC_OPTIONS.find((o) => o.key === tile.key)
    const formatted = option ? formatMetricValue(option, value, false) : numberFmt.format(value)
    return { label: tile.label, text: `${tile.prefix ?? ''}${formatted}` }
  }).filter((t): t is { label: string; text: string } => t !== null)

  // Work tab: all ingested industry + occupation keys (derived from the acs payload, sorted desc).
  // Nominal families (no inherent order) -> categorical viz.cat palette.
  const industryRows = shareRows(
    byKey,
    acs.map((a) => a.metricKey).filter((k) => k.startsWith('acs.industry.')),
    'nominal',
  )
  const occupationRows = shareRows(
    byKey,
    acs.map((a) => a.metricKey).filter((k) => k.startsWith('acs.occupation.')),
    'nominal',
  )

  // Ordered brackets -> viz.seq ordinal ramp. Nominal families -> viz.cat palette.
  const ageRows = shareRows(byKey, AGE_KEYS, 'ordinal')
  const raceRows = shareRows(byKey, RACE_KEYS, 'nominal')
  const educationRows = shareRows(byKey, EDUCATION_KEYS, 'ordinal')
  const economyBarRows = shareRows(byKey, ECONOMY_BAR_KEYS, 'nominal')
  const incomeTile = tiles.find((t) => t.label === 'Median HH income')

  return (
    <ProfileCard
      eyebrow="Electorate"
      title="Demographics (ACS 5-yr)"
      source="Census ACS 5-year, tracts aggregated to district"
    >
      {!hasAnything ? (
        <Text fontSize="sm" color="gray.500" mt={3}>
          No demographic data ingested for this geography.
        </Text>
      ) : (
        <Tabs.Root mt={2} size="sm" colorPalette="brand" lazyMount unmountOnExit defaultValue="overview">
          <Tabs.List overflowX="auto" overflowY="hidden" css={{ scrollbarWidth: 'none' }}>
            <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
            <Tabs.Trigger value="people">People</Tabs.Trigger>
            <Tabs.Trigger value="work">Work</Tabs.Trigger>
            <Tabs.Trigger value="economy">Economy</Tabs.Trigger>
          </Tabs.List>
          <Tabs.ContentGroup>
            {/* Overview */}
            <Tabs.Content value="overview" px={0}>
              {tiles.length > 0 ? (
                <SimpleGrid columns={{ base: 2, sm: 4 }} gap={4} mt={2}>
                  {tiles.map((t) => (
                    <StatTile key={t.label} label={t.label} value={t.text} />
                  ))}
                </SimpleGrid>
              ) : (
                <Text fontSize="sm" color="gray.400" mt={2}>
                  Not available for this geography.
                </Text>
              )}
            </Tabs.Content>

            {/* People */}
            <Tabs.Content value="people" px={0}>
              <ShareGroup caption="Key age bands" rows={ageRows} />
              <ShareGroup caption="Largest race/ethnicity groups" rows={raceRows} />
              <ShareGroup caption="Education (25+)" rows={educationRows} />
            </Tabs.Content>

            {/* Work */}
            <Tabs.Content value="work" px={0}>
              <ShareGroup caption="Industry mix (share of workers)" rows={industryRows} />
              <ShareGroup caption="Occupation groups" rows={occupationRows} />
            </Tabs.Content>

            {/* Economy */}
            <Tabs.Content value="economy" px={0}>
              {incomeTile && (
                <Box mb={4}>
                  <StatTile label={incomeTile.label} value={incomeTile.text} />
                </Box>
              )}
              <ShareGroup caption="Economic indicators" rows={economyBarRows} />
            </Tabs.Content>
          </Tabs.ContentGroup>
        </Tabs.Root>
      )}
    </ProfileCard>
  );
}
